import { useState } from 'react'
import { Button, Tooltip, Snackbar, IconButton } from '@mui/material'
import LinkedInIcon from '@mui/icons-material/LinkedIn'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'

const API_URL = import.meta.env.VITE_API_URL || '/api'

const CrossPublishButtons = ({ slug, content, title }) => {
  const [snackbar, setSnackbar] = useState({ open: false, message: '' })
  const [publishing, setPublishing] = useState({})

  const copyForLinkedIn = () => {
    const plainText = content
      .replace(/^#{1,6}\s+/gm, '')
      .replace(/\*\*(.*?)\*\*/g, '$1')
      .replace(/\*(.*?)\*/g, '$1')
      .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '$1 ($2)')
      .replace(/`{3}[\s\S]*?`{3}/g, '')
      .replace(/`([^`]+)`/g, '$1')
      .replace(/^[-*+]\s+/gm, '- ')
      .replace(/!\[.*?\]\(.*?\)/g, '')
      .trim()

    const linkedInText = `${title}\n\n${plainText}\n\n---\nOriginally published on promptedthoughts.dev/blog/${slug}`
    navigator.clipboard.writeText(linkedInText)
    setSnackbar({ open: true, message: 'Formatted for LinkedIn and copied to clipboard!' })
  }

  const copyForMedium = () => {
    const mediumText = `# ${title}\n\n${content}\n\n---\n*Originally published on [Prompted Thoughts](https://promptedthoughts.dev/blog/${slug})*`
    navigator.clipboard.writeText(mediumText)
    setSnackbar({ open: true, message: 'Markdown copied — paste directly into Medium!' })
  }

  const publishToLinkedIn = async () => {
    setPublishing(prev => ({ ...prev, linkedin: true }))
    try {
      const response = await fetch(`${API_URL}/blog-posts/${slug}/publish/linkedin`, {
        method: 'POST',
      })
      const data = await response.json()
      if (response.ok) {
        setSnackbar({ open: true, message: 'Published to LinkedIn!' })
      } else {
        copyForLinkedIn()
        setSnackbar({ open: true, message: data.error + ' — Copied to clipboard instead.' })
      }
    } catch {
      copyForLinkedIn()
    } finally {
      setPublishing(prev => ({ ...prev, linkedin: false }))
    }
  }

  const publishToMedium = async () => {
    setPublishing(prev => ({ ...prev, medium: true }))
    try {
      const response = await fetch(`${API_URL}/blog-posts/${slug}/publish/medium`, {
        method: 'POST',
      })
      const data = await response.json()
      if (response.ok) {
        setSnackbar({ open: true, message: `Published to Medium! ${data.mediumUrl}` })
      } else {
        copyForMedium()
        setSnackbar({ open: true, message: data.error + ' — Copied markdown to clipboard instead.' })
      }
    } catch {
      copyForMedium()
    } finally {
      setPublishing(prev => ({ ...prev, medium: false }))
    }
  }

  return (
    <>
      <div className="flex gap-2 items-center">
        <Tooltip title="Publish to LinkedIn (or copy formatted text)">
          <Button
            variant="outlined"
            size="small"
            startIcon={<LinkedInIcon />}
            onClick={publishToLinkedIn}
            disabled={publishing.linkedin}
            sx={{
              borderColor: '#0A66C2', color: '#0A66C2',
              '&:hover': { borderColor: '#0A66C2', backgroundColor: 'rgba(10,102,194,0.1)' },
            }}
          >
            LinkedIn
          </Button>
        </Tooltip>

        <Tooltip title="Copy LinkedIn plain text">
          <IconButton size="small" onClick={copyForLinkedIn} sx={{ color: '#0A66C2' }}>
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        <Tooltip title="Publish to Medium (or copy markdown)">
          <Button
            variant="outlined"
            size="small"
            onClick={publishToMedium}
            disabled={publishing.medium}
            sx={{
              borderColor: '#00AB6C', color: '#00AB6C',
              '&:hover': { borderColor: '#00AB6C', backgroundColor: 'rgba(0,171,108,0.1)' },
            }}
          >
            Medium
          </Button>
        </Tooltip>

        <Tooltip title="Copy Medium markdown">
          <IconButton size="small" onClick={copyForMedium} sx={{ color: '#00AB6C' }}>
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </div>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ open: false, message: '' })}
        message={snackbar.message}
      />
    </>
  )
}

export default CrossPublishButtons
